/**
 * <s-welcome-popup> — Welcome offer popup controller
 * Opens its child <dialog> once per visitor after `data-delay` seconds, remembers the
 * dismissal in localStorage, and after a successful customer signup
 * (`?customer_posted=true`) reopens showing the success region with the code and
 * applies that code to the shopper's cart so it shows at checkout automatically.
 *
 * Attributes:
 *   data-delay        — seconds before auto-open
 *   data-storage-key  — localStorage key that marks the popup as seen
 *   data-design-mode  — theme editor: never auto-open from the storefront rules
 *   data-preview      — theme editor: open immediately for editing
 *
 * Commands (on the dialog):
 *   --copy-code — copy the text of [data-welcome-code] to the clipboard
 */
class SWelcomePopup extends HTMLElement {
  #ac = null;
  #timer = null;
  #dialog = null;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const key = this.dataset.storageKey || 'welcome-popup-seen';

    this.#dialog.addEventListener('command', (e) => {
      if (e.command !== '--copy-code') return;
      this.#copyCode(e.source);
    }, opts);

    this.#dialog.addEventListener('close', () => {
      if (!this.hasAttribute('data-design-mode')) this.#remember(key);
    }, opts);

    if (this.hasAttribute('data-design-mode')) {
      if (this.hasAttribute('data-preview')) this.#open(true);
      return;
    }

    const posted = new URLSearchParams(window.location.search).get('customer_posted') === 'true';
    if (posted) {
      this.#showSuccess();
      this.#applyCode();
      this.#remember(key);
      this.#open(true);
      return;
    }

    if (this.querySelector('form [role="alert"]')) {
      this.#open(true);
      return;
    }

    if (this.#seen(key)) return;

    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    this.#timer = setTimeout(() => this.#open(false), delay);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  #open(force) {
    if (this.#dialog.open) return;
    // Don't stack on top of the cart, search, or menu unless the shopper just signed up.
    if (!force && document.querySelector('dialog[open]')) return;
    this.#dialog.showModal();
  }

  #showSuccess() {
    this.querySelector('[data-welcome-offer]')?.setAttribute('hidden', '');
    this.querySelector('[data-welcome-success]')?.removeAttribute('hidden');
  }

  // Save the code to the shopper's cart and browser so it applies at checkout without typing it.
  #applyCode() {
    const code = this.querySelector('[data-welcome-code]')?.textContent.trim();
    if (!code) return;
    const root = this.dataset.rootUrl || '/';
    const base = root.endsWith('/') ? root : `${root}/`;
    fetch(`${base}discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' }).catch(() => {});
  }

  #seen(key) {
    try {
      return window.localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  }

  #remember(key) {
    try {
      window.localStorage.setItem(key, '1');
    } catch {
      // Storage can be unavailable in private modes; the popup simply shows again next visit.
    }
  }

  async #copyCode(button) {
    const code = this.querySelector('[data-welcome-code]')?.textContent.trim();
    if (!code || !(button instanceof HTMLElement)) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      return;
    }
    const label = button.querySelector('[data-copy-label]');
    const copied = button.dataset.copiedLabel;
    if (!label || !copied) return;
    const original = label.textContent;
    label.textContent = copied;
    setTimeout(() => {
      label.textContent = original;
    }, 2000);
  }
}

if (!customElements.get('s-welcome-popup')) {
  customElements.define('s-welcome-popup', SWelcomePopup);
}
